/* eslint-disable no-nested-ternary */
/* eslint-disable no-lonely-if */
/* eslint-disable no-restricted-syntax */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Popover, Typography, Divider } from '@mui/material';
import { Icon } from '@iconify/react';

/**
 * buildExplanation
 *
 * Pure deterministic logic mapping a lead's treatments + contacted-counts to
 * a plain-language explanation of which bucket the lead is in and how to
 * move it forward. NO LLM, NO network calls — branch on data.
 *
 * Reachable states:
 *   1. No treatments at all → "New"
 *   2. Has treatments, none in NEW status → status reflects the treatment status
 *   3a. Single NEW treatment, count=0 → "New"
 *   3b. Single NEW treatment, count=N → "Follow up N" (capped at 3+)
 *   3c. Multiple NEW treatments → bucket = highest-count rule
 */
function buildExplanation(leadDetail, leadNotes = []) {
    const treatments = leadDetail?.lead_treatments || [];
    const NEW_BUCKET_LABELS = ['New', 'Follow up 1', 'Follow up 2', 'Follow up 3+'];
    const NEW_BUCKET_COLORS = {
        0: { bg: '#E8F5FE', fg: '#006C9C', accent: '#00B8D9' },   // info blue (New)
        1: { bg: '#FFF7CD', fg: '#7A4F01', accent: '#FFAB00' },   // amber (Follow up 1)
        2: { bg: '#FFE4DE', fg: '#7A0916', accent: '#FF5630' },   // coral (Follow up 2)
        3: { bg: '#FFE4DE', fg: '#7A0916', accent: '#B71D18' },   // red (Follow up 3+)
    };
    const ANOMALY_COLORS = { bg: '#FFEDDE', fg: '#7A2E0E', accent: '#FF5630' };  // alert orange
    const WHITELIST = ['UNABLE_TO_CONTACT', 'FOLLOW_UP', 'AWAITING_RESPONSE', 'NHS_WAITING_LIST'];

    // Surface the most-recent whitelist+channel note so the CTA can quote
    // real data instead of generic copy.
    const eligibleAttempts = (leadNotes || [])
        .filter((n) => WHITELIST.includes(n.note) && n.contacted_via)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    const attemptCount = eligibleAttempts.length;
    const latestAttempt = eligibleAttempts[0] || null;
    function formatAttemptDate(iso) {
        if (!iso) return '';
        try {
            // BE serializes timestamps with 6-digit microseconds
            // (e.g. "2026-04-27T10:27:50.068625+00:00"); JavaScript's Date
            // constructor only accepts up to 3-digit milliseconds. Trim the
            // fractional component to 3 digits before parsing.
            const trimmed = String(iso).replace(/\.(\d{3})\d+/, '.$1');
            const d = new Date(trimmed);
            if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10); // fallback: YYYY-MM-DD
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch (e) {
            return String(iso).slice(0, 10);
        }
    }

    function getFollowUpBucketIndex(count) {
        if (count <= 0) return 0;
        if (count === 1) return 1;
        if (count === 2) return 2;
        return 3;
    }

    function getName() {
        const first = (leadDetail?.first_name || '').trim();
        return first || 'This lead';
    }

    function getTreatmentName(t) {
        return (
            t?.treatment?.treatment_item?.name ||
            t?.treatment?.name ||
            null  // explicit null — let copy decide whether to mention by name
        );
    }

    const name = getName();
    const possessive = name === 'This lead' ? "This lead's" : `${name}'s`;

    // Case 1 — no treatments at all (webhook ingestion / direct API)
    if (treatments.length === 0) {
        // Case 1b — ANOMALY: no treatment, but contact attempts exist.
        // The bucket math says New, but the operator clearly tried to reach
        // this lead. Surface the inconsistency with a data-driven CTA
        // instead of justifying it as "normal."
        if (attemptCount > 0) {
            const expectedBucket = attemptCount >= 3 ? 'Follow up 3+'
                : attemptCount === 2 ? 'Follow up 2'
                    : 'Follow up 1';
            const latestExcerpt = (latestAttempt?.additional_notes || '').slice(0, 80).replace(/\s+/g, ' ').trim();
            const latestDate = formatAttemptDate(latestAttempt?.created_at);
            const channel = latestAttempt?.contacted_via || 'unknown channel';
            return {
                currentBucket: 'New',
                currentBucketIndex: 0,
                colors: ANOMALY_COLORS,
                count: 0,
                isAnomaly: true,
                why: (
                    `Heads up — this lead has ${attemptCount} contact ${attemptCount === 1 ? 'attempt' : 'attempts'} ` +
                    `logged but no treatment assigned, so the bucket can't progress. ` +
                    `Without a treatment the system has nothing to attribute the contact count to, ` +
                    `which is why ${name} is still showing in New.`
                ),
                latestAttempt: latestAttempt
                    ? `${latestDate} via ${channel}${latestExcerpt ? ` — "${latestExcerpt}"` : ''}`
                    : null,
                howToMoveForward: (
                    `Open the Treatments tab and assign a specific treatment (or "Other" as a placeholder). ` +
                    `Once a treatment exists, ${name} moves to ${expectedBucket} automatically based on the ` +
                    `${attemptCount} contact ${attemptCount === 1 ? 'attempt' : 'attempts'} already logged.`
                ),
            };
        }

        // Case 1a — calm/normal: no treatment AND no contact attempts yet.
        return {
            currentBucket: 'New',
            currentBucketIndex: 0,
            colors: NEW_BUCKET_COLORS[0],
            count: 0,
            why: (
                `${name} doesn't have a treatment assigned yet, so they're sitting in New ` +
                `until something happens. This is normal for leads created via WhatsApp inbound, ` +
                `web forms, imports, or other automated sources.`
            ),
            howToMoveForward: (
                `Use one of the contact buttons (WhatsApp, Email, or Phone) at the top of this page ` +
                `to reach out, then log what happened. The system will automatically assign ` +
                `"Other" as a placeholder treatment and ${name} will move to Follow up 1. ` +
                `You can change "Other" to a specific treatment from the Treatments tab whenever you know.`
            ),
        };
    }

    // Case 2 — has treatments, but none in NEW status (lead has progressed past New)
    const newTreatments = treatments.filter((t) => t.status === 'NEW');
    if (newTreatments.length === 0) {
        const primaryTreatment = treatments[0];
        const statusLabel = (primaryTreatment.status || 'unknown').replace(/_/g, ' ').toLowerCase();
        return {
            currentBucket: (primaryTreatment.status || 'Unknown').replace(/_/g, ' '),
            currentBucketIndex: -1,  // not in the New flow
            colors: { bg: '#E5F4EE', fg: '#0A5F44', accent: '#00AB55' },
            count: null,
            why: (
                `${possessive} treatment has progressed to ${statusLabel} status, so this lead is ` +
                `no longer in the New / Follow up flow. The label above reflects the treatment's ` +
                `current stage rather than a contact-attempt bucket.`
            ),
            howToMoveForward: (
                `${name} has moved past the New flow. To bring them back, change the treatment's ` +
                `status from the Treatments tab.`
            ),
        };
    }

    // Case 3 — at least one NEW treatment. Find the highest count among them.
    let maxCount = 0;
    let maxCountTreatment = null;
    for (const t of newTreatments) {
        const counts = t.lead_contacted_counts || [];
        for (const c of counts) {
            // eslint-disable-next-line no-continue
            if (c.status !== 'NEW') continue;
            if ((c.count || 0) > maxCount) {
                maxCount = c.count;
                maxCountTreatment = t;
            }
        }
    }

    const bucketIndex = getFollowUpBucketIndex(maxCount);
    const bucketLabel = NEW_BUCKET_LABELS[bucketIndex];
    const treatmentName = getTreatmentName(maxCountTreatment) || getTreatmentName(newTreatments[0]);

    let why;
    if (newTreatments.length === 1) {
        if (maxCount === 0) {
            // 3a — single NEW, no contact yet
            why = treatmentName
                ? `${possessive} ${treatmentName} enquiry hasn't been contacted yet. They'll move out of New as soon as you log a call, email, SMS, or WhatsApp message with a channel selected.`
                : `${name} hasn't been contacted yet. They'll move out of New as soon as you log a call, email, SMS, or WhatsApp message with a channel selected.`;
        } else {
            // 3b — single NEW, contacts logged
            const attemptWord = maxCount === 1 ? 'attempt' : 'attempts';
            why = treatmentName
                ? `You've logged ${maxCount} contact ${attemptWord} for ${possessive} ${treatmentName} enquiry, which puts them in ${bucketLabel}.`
                : `You've logged ${maxCount} contact ${attemptWord} for ${name}, which puts them in ${bucketLabel}.`;
        }
    } else {
        // 3c — multiple NEW treatments
        if (maxCount === 0) {
            why = `${name} has ${newTreatments.length} active enquiries, none of which have been contacted yet. That keeps them in New.`;
        } else {
            const attemptWord = maxCount === 1 ? 'attempt' : 'attempts';
            why = treatmentName
                ? `${name} has ${newTreatments.length} active enquiries. The most-followed-up one (${treatmentName}) has ${maxCount} contact ${attemptWord} logged, which puts ${name} in ${bucketLabel}.`
                : `${name} has ${newTreatments.length} active enquiries. The most-contacted one has ${maxCount} ${attemptWord} logged, which puts ${name} in ${bucketLabel}.`;
        }
    }

    let howToMoveForward;
    if (bucketIndex === 0) {
        howToMoveForward = (
            `Use the WhatsApp, Email, or Phone buttons at the top of this page to reach out, ` +
            `then log how it went. Your first contact attempt moves ${name} to Follow up 1.`
        );
    } else if (bucketIndex === 1) {
        howToMoveForward = `One more contact attempt and ${name} moves to Follow up 2.`;
    } else if (bucketIndex === 2) {
        howToMoveForward = `One more contact attempt and ${name} moves to Follow up 3+.`;
    } else {
        howToMoveForward = (
            `${name} is at Follow up 3+ — the last bucket in the New flow. ` +
            `If they're booking a consultation, log it from the Tasks tab. ` +
            `If they've gone unresponsive, consider archiving the lead instead of continuing to follow up.`
        );
    }

    return {
        currentBucket: bucketLabel,
        currentBucketIndex: bucketIndex,
        colors: NEW_BUCKET_COLORS[bucketIndex],
        count: maxCount,
        why,
        howToMoveForward,
    };
}

/**
 * BucketProgress — a 4-step visual showing where the lead sits across
 * New → Follow up 1 → Follow up 2 → Follow up 3+. Filled circles are
 * the buckets the lead has passed through; the current one has a ring.
 */
function BucketProgress({ bucketIndex, accentColor }) {
    const labels = ['New', 'FU 1', 'FU 2', 'FU 3+'];
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 0.5,
                mt: 1,
            }}
        >
            {labels.map((label, idx) => {
                const isPassed = idx < bucketIndex;
                const isCurrent = idx === bucketIndex;
                const isFuture = idx > bucketIndex;
                return (
                    <Box
                        key={label}
                        sx={{
                            textAlign: 'center',
                            py: 0.75,
                            borderRadius: '8px',
                            backgroundColor: isCurrent
                                ? accentColor
                                : isPassed
                                    ? `${accentColor}33` // 20% alpha
                                    : '#F4F6F8',
                            color: isCurrent ? '#FFFFFF' : isFuture ? '#919EAB' : '#454F5B',
                            fontSize: 11,
                            fontWeight: isCurrent ? 700 : 600,
                            border: isCurrent ? `1.5px solid ${accentColor}` : 'none',
                            transition: 'all 120ms ease',
                        }}
                    >
                        {label}
                    </Box>
                );
            })}
        </Box>
    );
}

BucketProgress.propTypes = {
    bucketIndex: PropTypes.number.isRequired,
    accentColor: PropTypes.string.isRequired,
};

export default function LeadBucketExplainer({ leadDetail = null, leadNotes = [], placement = 'bottom' }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    if (!leadDetail) return null;

    const explanation = buildExplanation(leadDetail, leadNotes);
    const showProgress = explanation.currentBucketIndex >= 0 && !explanation.isAnomaly;

    return (
        <>
            <IconButton
                size="small"
                aria-label={explanation.isAnomaly ? 'Anomaly detected — click for details' : 'Why is this lead in this bucket?'}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    ml: 0.5,
                    p: 0.25,
                    color: explanation.isAnomaly ? '#FF5630' : '#919EAB',
                    '&:hover': { color: explanation.isAnomaly ? '#B71D18' : '#637381' },
                }}
            >
                <Icon
                    icon={explanation.isAnomaly ? 'eva:alert-triangle-fill' : 'eva:question-mark-circle-outline'}
                    width={16}
                />
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: placement === 'top' ? 'top' : 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: placement === 'top' ? 'bottom' : 'top',
                    horizontal: 'center',
                }}
                marginThreshold={16}
                PaperProps={{
                    sx: {
                        width: 380,
                        maxWidth: 'calc(100vw - 32px)',
                        maxHeight: 'min(540px, calc(100vh - 96px))',
                        p: 0,
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',  // outer wraps; inner body scrolls
                    },
                }}
            >
                {/* Header — title + close. Sticky at top while body scrolls. */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 1.5,
                        borderBottom: '1px solid #F4F6F8',
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {explanation.isAnomaly && (
                            <Icon
                                icon="eva:alert-triangle-fill"
                                width={18}
                                style={{ color: '#FF5630', flexShrink: 0 }}
                            />
                        )}
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#212B36' }}>
                            {explanation.isAnomaly
                                ? 'Bucket anomaly detected'
                                : `Why is this lead in ${explanation.currentBucket}?`}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        aria-label="Close"
                        onClick={() => setAnchorEl(null)}
                        sx={{ p: 0.25 }}
                    >
                        <Icon icon="eva:close-fill" width={18} />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        px: 2.5,
                        py: 1.75,
                        flex: '1 1 auto',
                        overflowY: 'auto',
                        minHeight: 0,
                    }}
                >
                    {/* Current bucket — colored badge + (optional) progress strip */}
                    <Section label="CURRENT BUCKET">
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: '8px',
                                backgroundColor: explanation.colors.bg,
                                color: explanation.colors.fg,
                            }}
                        >
                            <Icon
                                icon={explanation.currentBucketIndex === 0 ? 'eva:bookmark-fill' : 'eva:phone-call-fill'}
                                width={15}
                                style={{ color: explanation.colors.accent }}
                            />
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    letterSpacing: 0.2,
                                }}
                            >
                                {explanation.currentBucket}
                            </Typography>
                            {explanation.count != null && (
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 500,
                                        color: explanation.colors.fg,
                                        opacity: 0.7,
                                        ml: 0.5,
                                    }}
                                >
                                    · {explanation.count} contact{explanation.count === 1 ? '' : 's'}
                                </Typography>
                            )}
                        </Box>

                        {showProgress && (
                            <BucketProgress
                                bucketIndex={explanation.currentBucketIndex}
                                accentColor={explanation.colors.accent}
                            />
                        )}
                    </Section>

                    <Section label="WHY">
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: '#454F5B',
                                lineHeight: 1.6,
                            }}
                        >
                            {explanation.why}
                        </Typography>
                    </Section>

                    {explanation.latestAttempt && (
                        <Section label="LATEST CONTACT ATTEMPT">
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 0.75,
                                    p: 1,
                                    borderRadius: '6px',
                                    backgroundColor: '#F4F6F8',
                                }}
                            >
                                <Icon
                                    icon="eva:clock-outline"
                                    width={14}
                                    style={{ color: '#637381', flexShrink: 0, marginTop: 2 }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: '#454F5B',
                                        lineHeight: 1.55,
                                    }}
                                >
                                    {explanation.latestAttempt}
                                </Typography>
                            </Box>
                        </Section>
                    )}

                    <Section label="HOW TO MOVE FORWARD">
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: '#454F5B',
                                lineHeight: 1.6,
                                whiteSpace: 'pre-line',
                            }}
                        >
                            {explanation.howToMoveForward}
                        </Typography>
                    </Section>

                    <Divider sx={{ my: 1.5, borderColor: '#F4F6F8' }} />

                    <Section label="WHAT COUNTS AS A CONTACT ATTEMPT" smaller>
                        <Box
                            component="ul"
                            sx={{
                                pl: 2.25,
                                m: 0,
                                fontSize: 12,
                                color: '#637381',
                                lineHeight: 1.65,
                                '& li': { mb: 0.25 },
                            }}
                        >
                            <li>A <strong>Follow Up</strong>, <strong>Unable to Contact</strong>, <strong>Awaiting Response</strong>, or <strong>NHS Waiting List</strong> note</li>
                            <li>WITH a channel selected: Phone, Email, SMS, or WhatsApp</li>
                        </Box>
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: '#919EAB',
                                fontStyle: 'italic',
                                mt: 1,
                                pl: 0.25,
                            }}
                        >
                            Internal notes without a channel are recorded but don&apos;t move the lead.
                        </Typography>
                    </Section>
                </Box>
            </Popover>
        </>
    );
}

function Section({ label, children = null, smaller = false }) {
    return (
        <Box sx={{ mb: smaller ? 0.75 : 1.25 }}>
            <Typography
                sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.7,
                    color: '#919EAB',
                    mb: 0.5,
                }}
            >
                {label}
            </Typography>
            {children}
        </Box>
    );
}

Section.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node,
    smaller: PropTypes.bool,
};

LeadBucketExplainer.propTypes = {
    leadDetail: PropTypes.object,
    leadNotes: PropTypes.array,
    placement: PropTypes.oneOf(['top', 'bottom', 'right', 'left']),
};

// Exported for unit tests
export { buildExplanation };
